import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiFailure } from './errors'

/**
 * 服务端状态统一走 TanStack Query，不自己写 useEffect + fetch。
 *
 * 实时数据靠 `/api/v1/events` 那一条 SSE 连接推过来再 invalidate，
 * 所以这里默认不轮询——「沉默是好消息」，也别让浏览器一直嗡嗡响。
 */
/**
 * 会话过期时把人送回登录页。
 *
 * **判据是 `kind`，不是去翻错误消息里有没有「登录」两个字。**
 * 按文案判分支，改一次文案就会悄悄失效——而失效的样子是「什么都没发生」。
 *
 * 用 `location.assign` 而不是 router 的 navigate：这个回调在 React 树外面，
 * 而且会话没了之后整棵树的缓存都该丢掉，整页重来最干净。
 */
function onFailure(error: unknown): void {
  if (!(error instanceof ApiFailure) || error.kind !== 'unauthorized') return
  if (window.location.pathname === '/auth') return
  window.location.assign('/auth')
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: onFailure }),
    mutationCache: new MutationCache({ onError: onFailure }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        /**
         * **只重试「没人应答」那一种，别的一次都不重试。**
         *
         * 后端已经答话了的时候再问一遍，答案不会变：401 不会因为再问一次
         * 就变成已登录，404 不会因为再问一次就长出那条路由。
         *
         * 而重试的代价全落在用户那一侧：重试期间查询停在 `pending`，
         * 界面上是骨架屏——**一个「还在加载」的样子**。于是「你没登录」
         * 这件已经确定的事，被一个毫无意义的等待挡了一两秒；
         * 标签页要是没在前台，那个等待还会一直挂着（重试的第二道闸是焦点）。
         *
         * 线上表现出来就是：**它知道自己没登录，但它先转圈给你看。**
         * 而转圈暗示的是「再等等」，正好是最错的那个暗示。
         *
         * 所以判据不是次数，是**有没有人答话**：`unreachable` 才重试。
         */
        retry: (failureCount, error) =>
          error instanceof ApiFailure && error.kind !== 'unreachable' ? false : failureCount < 1,
        /**
         * 默认的 `'online'` 会让「连不上」变成「永远转圈」。
         *
         * TanStack 在 `networkMode: 'online'` 下，**认为没网时不会让请求失败，
         * 而是把它无限期挂起**（`fetchStatus: 'paused'`，`status` 一直是 `pending`）。
         * 界面这边 `isPending` 永远为真，于是 `if (error) return <ScreenError/>`
         * **永远走不到**——用户看到的是一个永远转的圈，而不是一句「连不上」。
         *
         * 挂起的设计本身讲得通（真离线时重试没意义），坏在**「挂起」与「还在加载」
         * 在界面上长得一模一样**。这是这个仓库反复栽的那一类：失败的样子和正常的样子一样。
         *
         * 而「有没有网」这件事 `navigator.onLine` 本来就答不准：它说在线的时候
         * 后端可能是死的，它说离线的时候（切 VPN、连上要登录的 Wi-Fi）请求其实打得通。
         * 拿一个答不准的判断去决定「要不要把错误告诉用户」，赔率不对。
         *
         * 换成 `'always'`：该失败就失败，让错误走到屏上。
         *
         * ## 它没有解决、也不该解决的那一半
         *
         * 重试还有第二道闸是**焦点**（`retryer.js` 的 `canContinue`：
         * `focusManager.isFocused() && …`），`'always'` 管不着它。
         * 所以**标签页在后台时，失败的查询仍然会停在骨架屏上**，
         * 切回来才继续并报错。那个行为是对的（后台标签页重试没意义），
         * 而且它自己会好。
         *
         * **这一条值得写下来是因为我差点把它读错**：第一次是在一个
         * `visibilityState === 'hidden'` 的窗格里量的，于是看到「永远转圈」，
         * 差点报成「后端一挂，每一屏都永远转圈」。真相是前台正常、后台挂起。
         * 量之前先断言前提，这次的前提是「这个标签页是不是真的可见」。
         */
        networkMode: 'always',
      },
      mutations: {
        // 同上。一个点了没反应、也不报错的按钮，比一个报错的按钮糟得多
        networkMode: 'always',
      },
    },
  })
}
